import { $getSelection, $isRangeSelection, EditorConfig, LexicalNode, ParagraphNode, SerializedParagraphNode, Spread } from "lexical";

export interface CustomParagraphPayload {
  leftIndent?: number;
  rightIndent?: number;
  firstLineIndent?: number;
};

export type SerializedCustomParagraphNode = Spread<
  {
    leftIndent?: number;
    rightIndent?: number;
    firstLineIndent?: number;
  },
  SerializedParagraphNode
>

const applyIndentStyles = (dom: HTMLElement, node: CustomParagraphNode, delay=false) => {
  const nestedRoot = dom.closest('.nested-editor-input');
  if (nestedRoot) {
    return;
  }
  const source = node;
  const leftIndent = source.getLeftIndent();
  const rightIndent = source.getRightIndent();
  const firstLineIndent = source.getFirstLineIndent();
  dom.style.visibility = 'hidden';
  const style = () => {
    const indentationBaseValue = getComputedStyle(dom).getPropertyValue('--lexical-indent-base-value');
    if (indentationBaseValue) {
      dom.style.setProperty('text-indent', `calc(${firstLineIndent - leftIndent} * ${indentationBaseValue})`);
      dom.style.setProperty('padding-left', `calc(${leftIndent} * ${indentationBaseValue})`);
      dom.style.setProperty('padding-right', `calc(${rightIndent} * ${indentationBaseValue})`);
    }
    dom.style.visibility = 'visible';
  }
  if (delay) {
    setTimeout(() => {
      style();
    }, 0);
  } else {
    style();
  }
}


export class CustomParagraphNode extends ParagraphNode {
  __leftIndent: number;
  __rightIndent: number;
  __firstLineIndent: number;

  constructor(
    leftIndent?: number,
    rightIndent?: number,
    firstLineIndent?: number,
    key?: string,
  ) {
    super(key);
    let prevLeftIndent;
    let prevRightIndent;
    let prevFirstLineIndent;
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = selection.focus.getNode().getParent();
      if ($isCustomParagraphNode(node)) {
        prevLeftIndent = node.getLeftIndent();
        prevRightIndent = node.getRightIndent();
        prevFirstLineIndent = node.getFirstLineIndent();
      }
    }
    this.__leftIndent = leftIndent ?? prevLeftIndent ?? 0;
    this.__rightIndent = rightIndent ?? prevRightIndent ?? 0;
    this.__firstLineIndent = firstLineIndent ?? prevFirstLineIndent ?? 0;
  }

  static getType() {
    return "custom-paragraph";
  }

  static clone(node: CustomParagraphNode) {
    return new CustomParagraphNode(
      node.__leftIndent,
      node.__rightIndent,
      node.__firstLineIndent,
      node.__key,
    );
  }

  createDOM(config: EditorConfig) {
    const dom = super.createDOM(config);
    const classList = dom.classList;
    classList.add("NEDTheme__indentPortrait");
    applyIndentStyles(dom, this, true);
    return dom;
  }

  updateDOM(prevNode: CustomParagraphNode, dom: HTMLElement, config: EditorConfig) {
    const update = super.updateDOM(prevNode, dom, config)
    applyIndentStyles(dom, prevNode);
    return update;
  }

  static importJSON(json: SerializedCustomParagraphNode) {
    const {leftIndent, rightIndent, firstLineIndent, format, direction} = json;
    const node = $createCustomParagraphNode({leftIndent, rightIndent, firstLineIndent});
    node.setFormat(format);
    node.setDirection(direction);
    return node;
  }

  exportJSON(): SerializedCustomParagraphNode {
    return {
      ...super.exportJSON(),
      type: CustomParagraphNode.getType(),
      leftIndent: this.__leftIndent,
      rightIndent: this.__rightIndent,
      firstLineIndent: this.__firstLineIndent,
    }
  }

  setLeftIndent(indent: number) {
    const self = this.getWritable();
    self.__leftIndent = indent;
  }

  getLeftIndent() {
    const self = this.getLatest();
    return self.__leftIndent;
  }

  setRightIndent(indent: number) {
    const self = this.getWritable();
    self.__rightIndent = indent;
  }

  getRightIndent() {
    const self = this.getLatest();
    return self.__rightIndent;
  }

  setFirstLineIndent(indent: number) {
    const self = this.getWritable();
    self.__firstLineIndent = indent;
  }

  getFirstLineIndent() {
    const self = this.getLatest();
    return self.__firstLineIndent;
  }
}

export function $createCustomParagraphNode(
  payload?: CustomParagraphPayload,
): CustomParagraphNode {
  return new CustomParagraphNode(
    payload?.leftIndent,
    payload?.rightIndent,
    payload?.firstLineIndent,
  );
}

export function $isCustomParagraphNode(node: LexicalNode | null | undefined): node is CustomParagraphNode  {
  return node instanceof CustomParagraphNode;
}